import type { ServerEvent } from '@aw/contracts';

export type ReplyPreview = { conversationId: string; runId: string; text: string };

/**
 * Replies typing in (`message.preview`), kept only in memory and only while their run is
 * working. A preview stays until the thread shows the stored message for the same run (the
 * thread hides it then), and is dropped a little later, or at once when the run stops or fails.
 */
export class PreviewStore {
  private byRun = new Map<string, ReplyPreview>();
  private listeners = new Set<() => void>();
  private snapshots = new Map<string, ReplyPreview[]>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Previews in one conversation, oldest first; the same array until something changes. */
  forConversation(conversationId: string): ReplyPreview[] {
    const cached = this.snapshots.get(conversationId);
    if (cached) return cached;
    const list = [...this.byRun.values()].filter((p) => p.conversationId === conversationId);
    this.snapshots.set(conversationId, list);
    return list;
  }

  handle(event: ServerEvent): void {
    if (event.type === 'message.preview') {
      const { conversationId, runId, text } = event;
      this.set(runId, { conversationId, runId, text });
    } else if (
      event.type === 'message.created' &&
      event.message.author === 'assistant' &&
      event.message.runId
    ) {
      // The stored reply takes over once the thread has it; keep the preview until then. Only the
      // reply: the person's own question names the run too, and arrives before any preview.
      const runId = event.message.runId;
      setTimeout(() => this.drop(runId), 10_000);
    } else if (
      event.type === 'run.updated' &&
      (event.run.status === 'failed' ||
        event.run.status === 'stopped' ||
        event.run.status === 'budget_reached')
    ) {
      this.drop(event.run.id);
    }
  }

  clear(): void {
    if (this.byRun.size === 0) return;
    this.byRun.clear();
    this.changed();
  }

  private set(runId: string, preview: ReplyPreview): void {
    this.byRun.set(runId, preview);
    this.changed();
  }

  private drop(runId: string): void {
    if (this.byRun.delete(runId)) this.changed();
  }

  private changed(): void {
    this.snapshots.clear();
    for (const l of this.listeners) l();
  }
}
